import { useCallback, useState, useRef } from 'react';
import { DragSourceMonitor, DropTargetMonitor } from 'react-dnd';

import {
  useElement,
  useAfterInitialEffect,
  useTriggerableLayoutEffect,
  ForwardProps,
  OmitStrict
} from '@pega/cosmos-react-core';

import Draggable, { DraggableItem } from '../Draggable';
import Droppable from '../Droppable';

import { DragDropListProps } from './DragDropList.types';

interface DragDropListItem<T extends object = object> extends DraggableItem<T> {
  removeFromCurrent?: () => void;
  returnToSource?: (item: DraggableItem<T>) => void;
  normalizeDestination?: () => void;
  changeSource?: () => void;
  changeDestination?: () => void;
}

const middleYOfRect = ({ top, bottom }: DOMRect): number => top + (bottom - top) / 2;

const DragDropList = <T extends object = object>({
  accept,
  items,
  itemRenderer: ItemRenderer,
  emptyRenderer: EmptyRenderer,
  onChange,
  pullMode = 'remove',
  pushMode = 'insert',
  as: Component = 'ul',
  ...restProps
}: DragDropListProps<T> & ForwardProps) => {
  const [internalItems, setInternalItems] = useState<DragDropListItem<T>[]>(items);
  const itemRectsRef = useRef<DOMRect[] | null>(null);
  const [listEl, setListEl] = useElement<HTMLElement>();

  const getItemRects = useCallback((): DOMRect[] | null => {
    if (!listEl || listEl.children.length === 0) return null;
    return [...listEl.children].map(itemEl => {
      return itemEl.getBoundingClientRect();
    });
  }, [listEl]);

  useAfterInitialEffect(() => {
    setInternalItems(items);
  }, [items]);

  const triggerOnChange = useTriggerableLayoutEffect(
    useCallback(() => {
      onChange(internalItems);
    }, [onChange, internalItems])
  );

  const getInsertIndex = useCallback(
    (item: DraggableItem<T>, monitor: DropTargetMonitor | DragSourceMonitor): number => {
      itemRectsRef.current = getItemRects();
      const { current: itemRects } = itemRectsRef;
      let insertIndex = -1;
      const prevIndex = internalItems.findIndex(({ id }) => id === item.id);

      const clientXY = monitor.getClientOffset();
      if (!itemRects || !clientXY) return insertIndex;

      const dragPreviewRectTop = clientXY.y;
      const atHead = dragPreviewRectTop < middleYOfRect(itemRects[0]);
      const atTail = dragPreviewRectTop >= middleYOfRect(itemRects[itemRects.length - 1]);

      if (atHead) {
        insertIndex = 0;
      } else if (atTail) {
        insertIndex = prevIndex === itemRects.length - 1 ? prevIndex : itemRects.length;
      } else {
        itemRects.some((rect, i, rects) => {
          if (i === rects.length - 1) {
            if (prevIndex === i) insertIndex = i;
            return true;
          }
          const belowCurrent = dragPreviewRectTop >= middleYOfRect(rect);
          const aboveNext = dragPreviewRectTop < middleYOfRect(rects[i + 1]);
          if (belowCurrent && aboveNext) {
            if (prevIndex !== -1 && i >= prevIndex) insertIndex = i;
            else insertIndex = i + 1;
            return true;
          }
          return false;
        });
      }

      return insertIndex;
    },
    [getItemRects, internalItems]
  );

  const removeById = useCallback(
    (itemId: DraggableItem<T>['id']): void => {
      if (pullMode === 'remove') {
        setInternalItems(currentItems => currentItems.filter(({ id }) => id !== itemId));
      }
    },
    [pullMode]
  );

  const normalizeItems = useCallback(() => {
    setInternalItems(currentItems =>
      currentItems.map(({ id, data, type }) => ({
        id,
        data,
        type
      }))
    );
  }, []);

  const positionItems = useCallback(
    (item: DragDropListItem<T>, monitor: DropTargetMonitor): void => {
      if (!monitor.canDrop()) return;

      const prevIndex = internalItems.findIndex(({ id }) => id === item.id);

      if (prevIndex === -1) {
        item.removeFromCurrent?.();
        item.removeFromCurrent = () => {
          removeById(item.id);
        };
        item.normalizeDestination = normalizeItems;
        item.changeDestination = triggerOnChange;
      }

      let insertIndex = 0;
      if (pushMode === 'insert') insertIndex = getInsertIndex(item, monitor);
      if (pushMode === 'append') insertIndex = internalItems.length + 1;

      let newItems: DraggableItem<T>[];

      // Is the current being dragged is within its own list
      if (prevIndex !== -1) {
        if (insertIndex === prevIndex || pushMode !== 'insert') return;

        newItems = internalItems.filter(({ id }) => id !== item.id);
        newItems.splice(insertIndex, 0, item);

        if (typeof pushMode === 'function') setInternalItems(newItems.sort(pushMode));
        else setInternalItems(newItems);
      } else {
        newItems = [...internalItems];
        newItems.splice(insertIndex, 0, item);

        if (typeof pushMode === 'function') setInternalItems(newItems.sort(pushMode));
        else setInternalItems(newItems);
      }
    },
    [internalItems, pushMode, getInsertIndex, normalizeItems, triggerOnChange, removeById]
  );

  const onHover = positionItems;
  const onDrop = positionItems;

  const onBegin = useCallback(
    (itemId: DraggableItem<T>['id']) =>
      (): OmitStrict<DragDropListItem<T>, keyof DraggableItem<T>> => {
        const initialIndex = internalItems.findIndex(({ id }) => id === itemId);

        return {
          removeFromCurrent: () => {
            removeById(itemId);
          },
          returnToSource: (item: DraggableItem<T>) => {
            setInternalItems(currentItems => {
              const newItems = currentItems.filter(({ id }) => id !== itemId);
              newItems.splice(initialIndex, 0, item);
              return newItems;
            });
            normalizeItems();
          },
          normalizeDestination: normalizeItems,
          changeSource: triggerOnChange,
          changeDestination: triggerOnChange
        };
      },
    [internalItems, normalizeItems, triggerOnChange, removeById]
  );

  const onEnd = useCallback((item: DragDropListItem<T> | undefined, monitor: DragSourceMonitor) => {
    if (!item) return;

    if (monitor.didDrop()) {
      item.normalizeDestination?.();
      if (item.changeSource !== item.changeDestination) item.changeSource?.();
      item.changeDestination?.();
    } else {
      item.removeFromCurrent?.();
      item.returnToSource?.(item);
    }
  }, []);

  const emptyContent = EmptyRenderer ? <EmptyRenderer /> : null;

  return (
    <Droppable accept={accept} onHover={onHover} onDrop={onDrop}>
      {({ dropRef }) => {
        return (
          <Component
            ref={(el: HTMLElement) => {
              dropRef(el);
              setListEl(el);
            }}
            {...restProps}
          >
            {internalItems.length === 0
              ? emptyContent
              : internalItems.map(item => {
                  return (
                    <Draggable
                      key={item.id}
                      id={item.id}
                      type={item.type}
                      data={item.data}
                      onBegin={onBegin(item.id)}
                      onEnd={onEnd}
                    >
                      {({ dragRef, previewRef, collected }) => {
                        return (
                          <ItemRenderer
                            {...{
                              ...item,
                              dragRef,
                              previewRef,
                              ...collected,
                              isDragging: collected.isDragging || !!item.removeFromCurrent
                            }}
                          />
                        );
                      }}
                    </Draggable>
                  );
                })}
          </Component>
        );
      }}
    </Droppable>
  );
};

export default DragDropList;
